import { useState } from 'react';
import { downloadMaterial } from '../downloadUtils';
import { isValidVideoGenerationAspectRatio, calculateAspectRatio, isNonRasterOriginal } from '../materialUtils';
import { rasterErrorMessage, useEnsureRasterAsset } from '../../../../features/materials/ensureRasterAsset';
import { useToast } from '../../../../hooks/useToast';
import { useSafeNavigate, useSafeLocation } from '../../../../hooks/useSafeRouter';
import { brandConfig } from '../../../../config/brands';
import { absoluteAppUrl } from '../../../../utils/navigation';
import type { MaterialData } from '../types';
import type { GenerationAsset } from '../../../generation/state/useGenerationState';
import type { AudioReferenceUsage } from '../../../generation/state/types';
import { DEFAULT_GENERATION_MODEL } from '../../../generation/state/useGenerationState';
import type { CloudMaterial } from '../../../../features/materials/materialsSlice';
import { layerStackFromMaterial } from '../../../../features/motion/layerStack';
import { useUser } from '../../../../features/auth/useUser';
import { useCreditRates } from '../../../../features/organizations/hooks';
import { materialImportCreditsFor } from '../../../../features/videoEditor/materialImportRate';
import { createEditProject } from '../../../../features/videoEditor/api';
import { buildEditProjectFromMaterial } from '../../../../features/videoEditor/mapper/buildEditProjectFromMaterial';
import { formatDefaultProjectName } from '../../../../features/videoEditor/projectName';

/**
 * 素材プレビューフッターのアクション定義。
 *
 * desktop (ボタン列) と mobile (プルダウン) は **この配列だけ** を描画する。
 * 可視条件・活性条件・ハンドラを 1 箇所に集約し、両ツリーの同期ズレによるバグを構造的に防ぐ。
 */
export type MaterialActionGroup =
  /** 左端の削除ボタン */
  | 'delete'
  /** 広告入稿 (desktop では「広告」ドロップダウンにまとまる) */
  | 'ads'
  /** ダウンロード・シェア・保護 */
  | 'basic'
  /** 画像編集・AI修正依頼・Canva・YouTube・動画生成 */
  | 'edit'
  /** 共有 / 個人フォルダへの移動 */
  | 'folder'
  /** 右端の「選択素材に追加」 */
  | 'selection';

export interface MaterialAction {
  /** 一意キー (テストのアサート対象・React の key) */
  key: string;
  /** 表示ラベル。desktop でラベル併記するボタンと mobile メニューの既定文言 */
  label: string;
  /** mobile メニューでのみラベルが異なる場合に指定 (例: 保護済み → 保護を解除) */
  menuLabel?: string;
  /** アイコン画像のパス */
  icon: string;
  /** desktop の Tooltip 文言 (iconOnly のボタンに付く) */
  tooltip?: string;
  /** 表示するか */
  visible: boolean;
  /** 非活性か */
  disabled?: boolean;
  /** 非活性の理由 (desktop では Tooltip に出す) */
  disabledReason?: string;
  /** 配置グループ */
  group: MaterialActionGroup;
  /** desktop でラベルを出さずアイコンのみにするか */
  iconOnly?: boolean;
  /** desktop のボタン配色 */
  variant?: 'default' | 'danger' | 'sub';
  onClick: () => void;
}

export interface MaterialActionsBaseProps {
  material: MaterialData;
  isVideo: boolean;
  isTemporaryMaterial: boolean;
  canEdit: boolean;
  fullSizeImageUrl: string | null;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  showAddToSelectionButton: boolean;
  showShareButton: boolean;
  generationState: any; // GenerationStateの型
  editInCanva: (materialId: string, isTemporary: boolean, isVideo: boolean, thumbnailUrl?: string) => Promise<any>;
  isCanvaLoading: boolean;
  /** 動画素材のYouTubeアップロードを開く (利用可能なときのみ渡す) */
  onYouTubeUpload?: () => void;
  /** 画像素材からスクロール動画を作るモーダルを開く (生成キューがある画面でのみ渡す・docs/features/swipe-motion-clip-plan.md §11.4) */
  onScrollVideo?: () => void;
  onSetViewMode: (mode: 'preview' | 'edit') => void;
  onSetAdsUploadOpen: (open: boolean) => void;
  onClose: () => void;
  /**
   * 削除成功後の遷移先を親に委ねる。
   * 一覧を持つ親 (素材パネル) は次の素材へ送るために指定する。未指定なら従来どおり閉じる。
   */
  onDeleted?: (materialId: string) => void;
  onRequestRefresh?: (materialId: string) => void;
  getPreviewUrl: () => string | null;
  updateCloudMutation: any;
  updateTemporaryMutation: any;
  saveTemporaryMutation: any;
  deleteTemporaryMutation: any;
  deleteCloudMutation: any;
  moveToPersonalMutation: any;
  setMaterial: (material: MaterialData) => void;
  setIsMoveToPersonalConfirmOpen: (open: boolean) => void;
}

export interface UseMaterialActionsParams extends MaterialActionsBaseProps {
  /** Google 広告アップロードの起動 (useGoogleAdsAssetUpload) */
  googleAdsTrigger: () => void;
  /** Google 広告アップロードが送信中か */
  googleAdsPending: boolean;
}

/** 500MB を超えるファイルは Canva にアップロードできない */
const MAX_CANVA_UPLOAD_SIZE = 500 * 1024 * 1024;
/** スクロール動画の入口を活性にする縦長の下限 (高さ / 幅)。 */
export const SCROLL_VIDEO_MIN_RATIO = 1.2;

export function useMaterialActions({
  material,
  isVideo,
  isTemporaryMaterial,
  canEdit,
  fullSizeImageUrl,
  videoUrl,
  thumbnailUrl,
  showAddToSelectionButton,
  showShareButton,
  generationState,
  editInCanva,
  isCanvaLoading,
  onYouTubeUpload,
  onScrollVideo,
  onSetViewMode,
  onSetAdsUploadOpen,
  onClose,
  onDeleted,
  onRequestRefresh,
  getPreviewUrl,
  updateCloudMutation,
  updateTemporaryMutation,
  saveTemporaryMutation,
  deleteTemporaryMutation,
  deleteCloudMutation,
  moveToPersonalMutation,
  setMaterial,
  setIsMoveToPersonalConfirmOpen,
  googleAdsTrigger,
  googleAdsPending,
}: UseMaterialActionsParams): MaterialAction[] {
  const navigate = useSafeNavigate();
  const location = useSafeLocation();
  const toast = useToast();
  const ensureRasterAsset = useEnsureRasterAsset();
  const [isDownloading, setIsDownloading] = useState(false);
  // 「動画エディタで編集」(素材起点の編集プロジェクト) は platform admin 限定 (プレゼン動画取り込み計画 §4.1 段階1)。
  // useUploadFeedback と同じ判定 (LightMedia 組織の admin、またはパートナードメイン上の LightMedia admin 本人)
  const { isPlatformOrg, isAdmin, hasPlatformAdminMembership } = useUser();
  const isPlatformAdmin = (isPlatformOrg && isAdmin) || hasPlatformAdminMembership;
  const [isCreatingEditProject, setIsCreatingEditProject] = useState(false);
  const materialDurationSec = (material as { duration_seconds?: number | null }).duration_seconds ?? null;
  // 取り込み 1 回のポイント (尺別・計画 §6)。表示専用で、控除は BE が素材行の長さから同じ規則で決める
  const creditRates = useCreditRates();
  const importCredits = materialImportCreditsFor(creditRates.data?.rates, materialDurationSec);
  const importLabel = importCredits != null ? `動画エディタで編集 (${importCredits}ポイント)` : '動画エディタで編集';
  // 料金を表示できるまでは有料の取り込みを始めない (取得中・取得失敗・区分なし・未設定のいずれも)
  const importDisabledReason = !materialDurationSec
    ? '動画の長さを取得中です'
    : importCredits == null
      ? (creditRates.isPending ? '料金を取得しています' : '料金を確認できないため取り込めません (60分以内の動画・料金設定)')
      : undefined;

  // 簡素素材 UI (usodeki 等) は操作を ダウンロード/保護/画像編集/AIに修正依頼/Canva/削除
  // のみに絞る (広告・動画生成・フォルダ移動・YouTube・選択素材に追加 を隠す)
  const simpleMaterialsUi = brandConfig.features.simpleMaterialsUi ?? false;
  const isImageMaterial = !isVideo && (material.content_type ?? '').startsWith('image/');
  const isValidAspectRatio = isValidVideoGenerationAspectRatio(material.width, material.height);
  // スクロール動画は縦長の画像 (高さが幅の 1.2 倍以上) が対象。横長は入口を出すが非活性
  const isTallImage = !!material.width && !!material.height && material.height >= material.width * SCROLL_VIDEO_MIN_RATIO;
  const isFileTooLargeForCanva = (material.file_size_bytes || 0) > MAX_CANVA_UPLOAD_SIZE;
  const isDeletePending = deleteTemporaryMutation.isPending || deleteCloudMutation.isPending;

  const materialLayerStack = layerStackFromMaterial(material);
  /** この素材を生成 state のアセット (参照画像/参照音声) 形式に変換する。
   *  usage は addAsset 側が contentType から最終決定する (音声は reference_audio)。 */
  const buildImageAsset = (previewUrl: string): GenerationAsset => ({
    id: material.id,
    thumbnailUrl: previewUrl,
    type: isTemporaryMaterial ? 'temporary' : 'cloud',
    // 非ラスタ原本 (SVG) はラスタ版 (raster_url) だけを sourceUrl にする (原本 URL は AI に流さない)。
    // 未生成なら undefined のまま作り、addAsset の手前で ensureRasterAsset が取得する
    sourceUrl: (isNonRasterOriginal(material.content_type) ? material.raster_url : fullSizeImageUrl) || undefined,
    storageBucket: (material as CloudMaterial).storage_bucket || null,
    storagePath: (material as CloudMaterial).storage_path || null,
    contentType: material.content_type || null,
    usage: 'reference',
    asin: material.asin || null,
    // 商品 (プロジェクト) 紐付きの有無。素材一覧 API の has_product_links (material_product_links の有無) を使う。
    // 選択素材パネルのブランド解決や「商品情報が必要」判定が、生成画像を元にした AI修正依頼でも効くようにする
    hasProductLinks: (material as { has_product_links?: boolean }).has_product_links ?? !!material.asin,
    width: material.width || null,
    height: material.height || null,
    aspectRatio: calculateAspectRatio(material.width, material.height),
    durationSeconds: (material as { duration_seconds?: number | null }).duration_seconds ?? null,
    // 音声素材のみ: 選択素材チップの波形プレビューに出す表示テキスト
    audioText: (material.content_type ?? '').startsWith('audio/')
      ? (((material.generation_metadata as { narration_text?: string; song_title?: string } | null | undefined)?.narration_text
          || (material.generation_metadata as { narration_text?: string; song_title?: string } | null | undefined)?.song_title) ?? null)
      : null,
    // 参照音声の用途の既定値推定に使う (docs/features/reference-audio-roles-plan.md §2.5)。
    ...((material.content_type ?? '').startsWith('audio/')
      ? {
          sourceType: (material as { source_type?: string | null }).source_type ?? null,
          defaultAudioRole: (material as { default_audio_role?: AudioReferenceUsage | null }).default_audio_role ?? null,
        }
      : {}),
    // レイヤー素材 (SVG を解析した合成 PNG) の LayerStack (motion-graphics-mode-plan.md §3.3)
    ...(materialLayerStack ? { layerStack: materialLayerStack } : {}),
  });

  /**
   * 生成 state に渡す asset をラスタ版にそろえる (非ラスタ原本 (SVG) でラスタ版が未生成なら
   * `POST .../raster` で取得: features/materials/ensureRasterAsset.ts)。
   * 失敗は理由をトーストで出して null を返す (呼び出し側はそこで中断し、素材を黙って除外しない)。
   */
  const buildRasterAsset = async (previewUrl: string): Promise<GenerationAsset | null> => {
    try {
      return await ensureRasterAsset(buildImageAsset(previewUrl));
    } catch (error) {
      console.error('ラスタ版の取得に失敗:', error);
      toast.error(rasterErrorMessage(error));
      return null;
    }
  };

  const handleAddToSelection = async () => {
    if (!generationState) {
      toast.error('選択素材への追加は利用できません');
      return;
    }
    // 音声素材はサムネイルを持たないことが多いため、プレビューURLが無ければ
    // 本体URL (署名付き音声URL) で代替する (サムネイル表示側は音声プレースホルダー)。
    const isAudioMaterial = (material.content_type ?? '').startsWith('audio/');
    const previewUrl = getPreviewUrl() || (isAudioMaterial ? fullSizeImageUrl : null);
    if (previewUrl) {
      const asset = await buildRasterAsset(previewUrl);
      if (!asset) return;
      generationState.addAsset(asset);
      toast.show('選択素材に追加しました');
      onClose();
    }
  };

  const handleDownload = async () => {
    const downloadUrl = videoUrl || fullSizeImageUrl;
    if (downloadUrl && !isDownloading) {
      setIsDownloading(true);
      try {
        await downloadMaterial(material, downloadUrl);
        toast.show('ファイルをダウンロードしました');
      } catch (error) {
        console.error('ダウンロードエラー:', error);
        toast.show('ダウンロードに失敗しました');
      } finally {
        setIsDownloading(false);
      }
    }
  };

  const handleShare = async () => {
    // 新URL構造: /materials/personal または /materials/shared + ?detail=xxx
    const folderPath = isTemporaryMaterial ? 'personal' : 'shared';
    // VITE_APP_BASENAME=/app などサブパス配信時に /app プレフィックスを取りこぼさないよう absoluteAppUrl を経由する
    const shareUrl = `${absoluteAppUrl(`/materials/${folderPath}`)}?detail=${material.id}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: '素材をシェア',
          url: shareUrl,
        });
        toast.show('シェアしました');
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.show('URLをコピーしました');
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('シェアエラー:', error);
      toast.error('シェアに失敗しました');
    }
  };

  const handleProtectionToggle = async () => {
    try {
      const newProtected = !material.protected;
      if (isTemporaryMaterial) {
        await updateTemporaryMutation.mutateAsync({ protected: newProtected });
      } else {
        await updateCloudMutation.mutateAsync({ protected: newProtected });
      }
      setMaterial({ ...material, protected: newProtected });
      toast.show(newProtected ? '素材を保護しました' : '保護を解除しました');
      onRequestRefresh?.(material.id);
    } catch (error) {
      console.error('Protection toggle error:', error);
      toast.error('保護状態の更新に失敗しました');
    }
  };

  const handleImageEdit = () => {
    if (!canEdit) {
      toast.show('編集は利用できません');
      return;
    }
    onSetViewMode('edit');
  };

  const handleAiEditRequest = async () => {
    if (!generationState) {
      toast.error('AI修正依頼は利用できません');
      return;
    }
    // ラスタ版の取得は選択素材を消す前に済ませる (失敗したら現状のまま中断する)
    const previewUrl = getPreviewUrl();
    const asset = previewUrl ? await buildRasterAsset(previewUrl) : null;
    if (previewUrl && !asset) return;

    generationState.clearAssets();
    if (asset) {
      generationState.addAsset(asset);
    }

    // AI 修正依頼で遷移するときの既定値: GPT Image (DEFAULT_GENERATION_MODEL) 以外は何も指定しない状態にする。
    //  - プラットフォーム / アスペクト比 / 出力形式: 指定なし
    //  - リサイズ (auto_trim_resolution): しない
    //  - モデル: GPT Image 2.5 Flare 自動 (DEFAULT_GENERATION_MODEL。2026-09-08 に gpt-image-2-auto から切替)
    //  - 解像度: GPT Image は supported_resolutions を持たないため解像度セレクタ自体が
    //    非表示になり、image_size は無視される (=「無選択」)。元画像に追従して出力される。
    // generation_model / aspect_ratio / media_platform_id は通常 localStorage に永続化
    // されるが、AI 修正依頼の既定値はこのフロー限定の一時的なものなので、これらのキーは
    // 永続化をスキップしてユーザーの恒久設定 (モデル選好・最終プラットフォーム等) を汚さない。
    generationState.applyTransientSettings(
      {
        media_platform_id: null,
        media_menu_id: null,
        media_spec_id: null,
        media_guidelines: [],
        media_menu_guidelines: [],
        aspect_ratio: 'none',
        auto_trim_resolution: false,
        generation_model: DEFAULT_GENERATION_MODEL,
        output_format: undefined,
        image_background: undefined,
        // バナーレイアウトは引き継がない (完成画像が配置を持っており、ガイドを重ねると修正指示と衝突する。
        // docs/features/banner-layout-template-plan.md §4)
        banner_layout: null,
        // 「AIに送る情報」トグル (ロゴ / 商品情報 / ブランド情報) は利用者の保存値 (localStorage) をそのまま使う。
        // 元画像のブランドが解決できればプロンプト入力モードにトグルが出て、ON なら元画像のロゴを添付し維持指示を付ける
      },
      ['generation_model', 'aspect_ratio', 'media_platform_id'],
    );
    generationState.setPendingEdit('');

    // スタジオの静止画プロンプト入力モードに遷移 (/studio 配下にいるときは useStudioRouting が pendingMode を見て切り替える)
    if (!location.pathname.startsWith('/studio')) {
      navigate('/studio/image/prompt');
      setTimeout(() => {
        onClose();
      }, 100);
    } else {
      onClose();
    }
  };

  const handleVideoGeneration = async () => {
    if (!generationState) {
      toast.error('動画生成は利用できません');
      return;
    }

    // ラスタ版の取得は選択素材を消す前に済ませる (失敗したら現状のまま中断する)
    const previewUrl = getPreviewUrl();
    const asset = previewUrl ? await buildRasterAsset(previewUrl) : null;
    if (previewUrl && !asset) return;

    generationState.clearAssets();
    if (asset) {
      generationState.addAsset(asset);
    }

    generationState.setPendingVideoGeneration();

    // スタジオの動画生成に遷移
    if (!location.pathname.startsWith('/studio/video')) {
      navigate('/studio/video');
      setTimeout(() => {
        onClose();
      }, 100);
    } else {
      onClose();
    }
  };

  // 動画素材の「AIに修正依頼」: 動画プロンプト入力モードへ切り替え、この動画を
  // 参照動画 (video-to-video) として編集させる。モデルは現在の選択を尊重し、
  // v2v 非対応時のみ beginVideoEdit 内で対応モデルへ自動切替する。
  const handleVideoAiEditRequest = () => {
    if (!generationState) {
      toast.error('AI修正依頼は利用できません');
      return;
    }

    const asset: GenerationAsset = {
      id: material.id,
      thumbnailUrl: thumbnailUrl || material.thumbnail_url || material.thumbnailUrl || '',
      type: isTemporaryMaterial ? 'temporary' : 'cloud',
      sourceUrl: videoUrl || undefined,
      storageBucket: (material as CloudMaterial).storage_bucket || null,
      storagePath: (material as CloudMaterial).storage_path || null,
      contentType: material.content_type || 'video/mp4',
      usage: 'reference_video',
      asin: material.asin || null,
      width: material.width || null,
      height: material.height || null,
      aspectRatio: calculateAspectRatio(material.width, material.height),
    };
    // beginVideoEdit がモデル解決・参照モード切替・素材追加をまとめて行う。
    generationState.beginVideoEdit(asset);

    // 動画プロンプト入力モードへ遷移
    if (!location.pathname.startsWith('/studio/video')) {
      navigate('/studio/video/prompt');
      setTimeout(() => {
        onClose();
      }, 100);
    } else {
      navigate('/studio/video/prompt');
      onClose();
    }
  };

  // 動画素材から直接、動画エディタの編集プロジェクトを作って開く (シナリオなし。計画 §4.1)。
  // request_id はこのボタン押下ごとに発行し、作成 (POST /video-edit-projects) と遷移先での取り込み受付
  // (POST /:id/import) で同じ値を使う。同じ ID の再送には BE が既存プロジェクトを返すので二重作成にならない。
  const handleVideoEditorImport = async () => {
    if (isCreatingEditProject || !materialDurationSec || importCredits == null) return;
    setIsCreatingEditProject(true);
    try {
      const requestId = crypto.randomUUID();
      const built = buildEditProjectFromMaterial(
        {
          id: material.id,
          isTemporary: isTemporaryMaterial,
          content_type: material.content_type,
          duration_seconds: materialDurationSec,
          width: material.width,
          height: material.height,
          name: material.name,
        },
        { name: formatDefaultProjectName() },
      );
      const created = await createEditProject({
        origin: 'material',
        materialId: material.id,
        isTemporary: isTemporaryMaterial,
        requestId,
        name: built.name,
        project: built,
      });
      // 入口で表示した料金 (quoted) を引き継ぎ、エディタの初回取り込みが同じ額を quoted_credits として送る
      navigate(`/studio/video/editor/${created.id}`, { state: { importRequestId: requestId, importQuotedCredits: importCredits } });
      onClose();
    } catch (error) {
      console.error('[useMaterialActions] Failed to create edit project from material:', error);
      toast.error('動画エディタの編集を作成できませんでした');
    } finally {
      setIsCreatingEditProject(false);
    }
  };

  const handleCanvaEdit = async () => {
    // 500MBを超えるファイルはCanvaにアップロード不可
    if (isFileTooLargeForCanva) {
      toast.error('500MB以上のファイルはCanvaに送ることはできません。ダウンロードしてファイルサイズを小さくしてから直接Canvaにアップロードしてください。');
      return;
    }

    try {
      // 動画の場合はサムネイルURLを渡す（material.thumbnail_urlを優先）
      const canvaThumbnailUrl = isVideo
        ? (material.thumbnail_url || getPreviewUrl() || undefined)
        : undefined;
      await editInCanva(material.id, isTemporaryMaterial, isVideo, canvaThumbnailUrl);
    } catch (error: any) {
      console.error('Canva edit error:', error);
      if (!error.response?.data?.need_auth) {
        toast.error('Canvaで編集できませんでした');
      }
    }
  };

  const handleMoveToSharedFolder = async () => {
    try {
      await saveTemporaryMutation.mutateAsync(material.id);
      toast.show('共有フォルダへ移動しました');
      onClose();
    } catch (error) {
      console.error('共有フォルダへの移動エラー:', error);
      toast.show('共有フォルダへの移動に失敗しました');
    }
  };

  const handleDelete = async () => {
    // 保護素材はボタン自体を非活性にしているが、ここでも最終ガードを持つ
    if (material.protected) {
      toast.show('保護されている素材は削除できません');
      return;
    }
    try {
      if (isTemporaryMaterial) {
        await deleteTemporaryMutation.mutateAsync(material.id);
      } else {
        await deleteCloudMutation.mutateAsync(material.id);
      }
      toast.show('素材を削除しました');
      // 削除後にリフレッシュコールバックを呼ぶ（ジョブ履歴を更新するため）
      if (onRequestRefresh) {
        onRequestRefresh(material.id);
      }
      // 一覧を持つ親は次の素材へ送る。最後の 1 件だった場合は親が閉じる
      if (onDeleted) {
        onDeleted(material.id);
      } else {
        onClose();
      }
    } catch (error: unknown) {
      console.error('削除エラー:', error);
      // お気に入りシナリオで使用中の場合は詳細なエラーメッセージを表示
      if (error instanceof Error && 'scenario_titles' in error) {
        const scenarioTitles = (error as Error & { scenario_titles?: string[] }).scenario_titles;
        if (scenarioTitles && scenarioTitles.length > 0) {
          toast.error(`この素材はお気に入りシナリオ「${scenarioTitles[0]}」で使用中のため削除できません`);
          return;
        }
      }
      toast.show('削除に失敗しました');
    }
  };

  return [
    // --- 広告入稿 (desktop は「広告」ドロップダウンにまとめる) ---
    {
      key: 'amazon-ads',
      label: 'Amazon広告',
      menuLabel: 'Amazon広告に入稿',
      icon: '/badge_ad.svg',
      group: 'ads',
      visible: !simpleMaterialsUi && brandConfig.features.amazonIntegration,
      onClick: () => onSetAdsUploadOpen(true),
    },
    // Google 広告は単一項目。クリック時、複数アカウントが連携済みならピッカーモーダルを開く。
    // 1 件なら直接アップロード、0 件なら誘導。非 admin にも見せるが、クリック時に権限不足を案内する。
    {
      key: 'google-ads',
      label: googleAdsPending ? 'Google広告 (送信中…)' : 'Google広告',
      menuLabel: googleAdsPending ? 'Google広告 (送信中…)' : 'Google広告に入稿',
      icon: '/badge_ad.svg',
      group: 'ads',
      visible: !simpleMaterialsUi && brandConfig.features.googleIntegration,
      disabled: !isImageMaterial || googleAdsPending,
      disabledReason: !isImageMaterial ? 'Google 広告は画像素材のみ対応しています' : undefined,
      onClick: googleAdsTrigger,
    },

    // --- 基本操作 ---
    {
      key: 'download',
      label: isDownloading ? 'ダウンロード中...' : 'ダウンロード',
      menuLabel: 'ダウンロード',
      icon: '/download_bottom.svg',
      tooltip: isDownloading ? 'ダウンロード中...' : 'ダウンロード',
      group: 'basic',
      visible: true,
      disabled: (!fullSizeImageUrl && !videoUrl) || isDownloading,
      iconOnly: true,
      onClick: () => { void handleDownload(); },
    },
    {
      key: 'share',
      label: 'シェア',
      icon: '/share.svg',
      tooltip: 'シェア',
      group: 'basic',
      visible: showShareButton,
      iconOnly: true,
      onClick: () => { void handleShare(); },
    },
    {
      key: 'protect',
      label: material.protected ? '保護済み' : '保護する',
      // メニューでは操作の結果 (保護を解除する) を示す文言にする
      menuLabel: material.protected ? '保護を解除' : '保護する',
      icon: material.protected ? '/lock.svg' : '/lock_unlock_4.svg',
      tooltip: material.protected ? '保護済み' : '保護する',
      group: 'basic',
      visible: true,
      iconOnly: true,
      onClick: () => { void handleProtectionToggle(); },
    },

    // --- 編集系 ---
    {
      key: 'edit-image',
      label: '画像編集',
      icon: '/scissors_2.svg',
      group: 'edit',
      visible: !simpleMaterialsUi && !isVideo,
      onClick: handleImageEdit,
    },
    {
      key: 'ai-edit-image',
      label: 'AIに修正依頼',
      icon: '/content_pen_write.svg',
      group: 'edit',
      visible: !isVideo,
      disabled: !generationState,
      disabledReason: !generationState ? 'AI修正依頼は利用できません' : undefined,
      onClick: () => { void handleAiEditRequest(); },
    },
    {
      key: 'ai-edit-video',
      label: 'AIに修正依頼',
      icon: '/content_pen_write.svg',
      group: 'edit',
      visible: isVideo,
      disabled: !generationState,
      disabledReason: !generationState ? 'AI修正依頼は利用できません' : undefined,
      onClick: handleVideoAiEditRequest,
    },
    {
      key: 'canva',
      label: isCanvaLoading ? 'Canva認証中...' : (isVideo ? 'Canvaへ送る' : 'Canvaで編集'),
      icon: '/canva_logo.svg',
      tooltip: 'Canvaに送って編集',
      group: 'edit',
      visible: brandConfig.features.canvaIntegration,
      disabled: isCanvaLoading,
      // 動画はアイコンのみ (ラベル併記だとフッターが溢れる)
      iconOnly: isVideo,
      onClick: () => { void handleCanvaEdit(); },
    },
    {
      key: 'video-editor-import',
      label: isCreatingEditProject ? '編集を作成中...' : importLabel,
      menuLabel: importLabel,
      // 既存の動画系アクションと同じアイコンを流用 (新しい SVG は作らない)
      icon: '/video_player_movie.svg',
      tooltip: importLabel,
      group: 'edit',
      visible: !simpleMaterialsUi && isVideo && isPlatformAdmin,
      disabled: isCreatingEditProject || importDisabledReason != null,
      disabledReason: importDisabledReason,
      iconOnly: true,
      onClick: () => { void handleVideoEditorImport(); },
    },
    {
      key: 'youtube',
      label: 'YouTubeにアップロード',
      icon: '/youtube_logo.svg',
      tooltip: 'YouTubeにアップロード',
      group: 'edit',
      visible: isVideo && !!onYouTubeUpload,
      iconOnly: true,
      onClick: () => onYouTubeUpload?.(),
    },
    {
      key: 'scroll-video',
      label: 'スクロール動画',
      icon: '/website_scroll.svg',
      tooltip: 'スクロール動画を生成',
      group: 'edit',
      visible: !simpleMaterialsUi && isImageMaterial && !!onScrollVideo,
      disabled: !isTallImage,
      disabledReason: '縦長の画像 (高さが幅の1.2倍以上) で使えます',
      iconOnly: true,
      onClick: () => onScrollVideo?.(),
    },
    {
      key: 'video-generation',
      label: 'この画像から動画生成',
      icon: '/video_player_movie.svg',
      tooltip: 'この画像から動画生成',
      group: 'edit',
      visible: !simpleMaterialsUi && !isVideo,
      disabled: !isValidAspectRatio,
      disabledReason: '動画生成するためにはアスペクト比を16:9もしくは9:16に切り出してください',
      iconOnly: true,
      onClick: () => { void handleVideoGeneration(); },
    },

    // --- フォルダ移動 ---
    {
      key: 'move-to-shared',
      label: saveTemporaryMutation.isPending ? '移動中...' : '共有フォルダへ移動',
      icon: '/cloud_file.svg',
      group: 'folder',
      visible: !simpleMaterialsUi && isTemporaryMaterial,
      disabled: saveTemporaryMutation.isPending,
      onClick: () => { void handleMoveToSharedFolder(); },
    },
    {
      key: 'move-to-personal',
      label: moveToPersonalMutation.isPending ? '移動中...' : '個人フォルダへ移動',
      icon: '/single_neutral_folder_box.svg',
      group: 'folder',
      visible: !simpleMaterialsUi && !isTemporaryMaterial,
      disabled: moveToPersonalMutation.isPending,
      onClick: () => setIsMoveToPersonalConfirmOpen(true),
    },

    // --- 選択素材に追加 ---
    {
      key: 'add-to-selection',
      label: '選択素材に追加',
      icon: '/coding_apps_website_apps_add_widget.svg',
      group: 'selection',
      visible: showAddToSelectionButton && !isVideo,
      disabled: !generationState,
      disabledReason: !generationState ? '選択素材への追加は利用できません' : undefined,
      variant: 'sub',
      onClick: () => { void handleAddToSelection(); },
    },

    // --- 削除 ---
    {
      key: 'delete',
      label: isDeletePending ? '削除中...' : '削除',
      icon: '/bin_1.svg',
      group: 'delete',
      visible: true,
      disabled: material.protected || isDeletePending,
      disabledReason: material.protected ? '保護されている素材は削除できません' : undefined,
      variant: 'danger',
      onClick: () => { void handleDelete(); },
    },
  ];
}
